import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveArticle, unsaveArticle } from '../article.api';
import ToastBtnMessage from '../../components/ToastBtnMessage';
import React from 'react';
import DataLayer from '../../utils/data-layer';

export const useBookmarkArticleMutation = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      articleId,
      isSaved,
      article
    }: {
      articleId: string;
      isSaved: boolean;
      article?: any;
    }) => {
      if (isSaved) {
        return unsaveArticle(articleId);
      }
      return saveArticle(articleId);
    },
    onSuccess: (_, { isSaved, articleId, article }) => {
      const message = isSaved
        ? 'Article has been removed from your Reading List successfully!'
        : 'Article has been saved to your Reading List successfully!';

      // Track article bookmark event
      if (articleId && article && !isSaved) {
        const articleType = article.writingType?.name;
        DataLayer.trackArticleBookmark(articleType, articleId);
      }

      toast({
        render: () => (
          <ToastBtnMessage
            title={message}
            isError={false}
            color="jet-black"
            btnTitle="View Reading List"
            btnAction={() => {
              window.location.href = '/?tab=myReading';
            }}
          ></ToastBtnMessage>
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });

      // Invalidate the article query to refetch with updated saved status
      queryClient.invalidateQueries({ queryKey: ['article'] });
    },
    onError: (error, { articleId, isSaved, article }) => {
      const message = isSaved
        ? "Couldn't remove article from your Reading List!"
        : "Couldn't save article to your Reading List!";

      toast({
        render: () => (
          <ToastBtnMessage
            title={message}
            isError={true}
            color="jet-black"
            btnTitle="retry"
            btnAction={() => mutation.mutate({ articleId, isSaved })}
          />
        ),
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
      console.error(error);
    }
  });

  return mutation;
};
